import { Alert } from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { rolesNeedZoomSeat } from '@/schemas/zoom.schema';
import type { ZoomSeatUsage } from '@/schemas/zoom.schema';

export interface ZoomSeatCapacityAlertProps {
  selectedRoles: readonly string[];
  usage: ZoomSeatUsage | null;
  loading?: boolean;
  error?: string | null;
}

export function ZoomSeatCapacityAlert({
  selectedRoles,
  usage,
  loading = false,
  error = null,
}: ZoomSeatCapacityAlertProps): JSX.Element | null {
  if (!rolesNeedZoomSeat(selectedRoles)) {
    return null;
  }

  if (loading) {
    return (
      <Alert variant="light" color="blue" icon={<IconInfoCircle />} title="Zoom telehealth licenses">
        Loading seat availability…
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert variant="light" color="yellow" icon={<IconAlertTriangle />} title="Zoom seat availability">
        {error}
      </Alert>
    );
  }

  if (!usage) {
    return null;
  }

  const exhausted = usage.available <= 0;

  if (exhausted) {
    return (
      <Alert variant="light" color="yellow" icon={<IconAlertTriangle />} title="No Zoom licenses available">
        No Zoom telehealth licenses are available ({usage.usage} assigned
        {usage.pending > 0 ? `, ${usage.pending} pending invite${usage.pending === 1 ? '' : 's'}` : ''},{' '}
        {usage.hosts} purchased). Purchase additional seats in Zoom Admin, then create or assign this
        user again. Unify will not create the user until a seat is free.
      </Alert>
    );
  }

  return (
    <Alert variant="light" color="blue" icon={<IconInfoCircle />} title="Zoom telehealth licenses">
      {usage.available} of {usage.hosts} Zoom telehealth licenses available
      {usage.pending > 0 ? ` (${usage.pending} pending invite${usage.pending === 1 ? '' : 's'})` : ''}.
    </Alert>
  );
}
